import { useEffect, useState, type ReactNode } from "react"
import { Link, NavLink, useLocation } from "react-router"
import { ArrowUpRight, ExternalLink, Mail, Menu, MessageCircle, X } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import { Logo } from "@/components/Logo"
import { ThemeToggle } from "@/components/ThemeToggle"
import { Button } from "@/components/ui/button"
import { livePlatformUrl, navigation } from "@/data/site"

export function Layout({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    setOpen(false)
    window.scrollTo({ top: 0, behavior: "smooth" })

    const pageTitles: Record<string, string> = {
      "/": "Smart Drain | Stop litter. Monitor the flow. Act before the flood",
      "/technology": "Smart Drain Technology | Physical protection + digital intelligence",
      "/products": "Smart Drain Products | Inlet filters & open-drain retrofits",
      "/initiative": "Global Smart Drain Initiative | Climate infrastructure for cities",
      "/deployments": "Smart Drain Deployments | Kolonnawa field implementation",
      "/pilot": "Smart Drain Pilot Program | How the pilot works",
      "/start-pilot": "Start a Smart Drain Pilot",
      "/development": "Smart Drain Development Roadmap",
      "/about": "About Smart Drain | Urban Inventors",
      "/team": "Smart Drain Team | Urban Inventors",
      "/partners": "Smart Drain Partners & Ecosystem",
      "/gallery": "Smart Drain Gallery",
      "/blog": "Smart Drain Blog | Field evidence & technology",
      "/contact": "Contact Smart Drain | Pilots & partnerships",
    }
    document.title = pageTitles[location.pathname] ?? (location.pathname.startsWith("/blog/") ? "Smart Drain Blog" : "Smart Drain")
  }, [location.pathname])

  return (
    <div className="min-h-screen bg-[#f7faf9] text-slate-900 transition-colors duration-300 dark:bg-[#0a1214] dark:text-slate-100">
      <header className="fixed inset-x-0 top-0 z-50">
        <div className="border-b border-white/10 bg-[#04181b] text-white backdrop-blur-xl">
          <div className="mx-auto flex min-h-8 max-w-7xl items-center justify-start gap-4 overflow-x-auto px-6 py-2 text-[9px] font-semibold uppercase tracking-[.14em] text-white/65 no-scrollbar sm:justify-between sm:text-[10px]">
            <a
              href="https://good-design.org/projects/solid-waste-clog-resistant-smart-drain/"
              target="_blank"
              rel="noreferrer"
              className="group inline-flex shrink-0 items-center gap-1.5 transition-colors hover:text-emerald-300 hover:underline underline-offset-4"
            >
              <span>Australian Good Design — Best in Class 2024</span>
              <ExternalLink className="size-2.5 opacity-50 transition-opacity group-hover:opacity-100" />
            </a>
            <a
              href="https://www.wipo.int/en/web/awards/global/2025"
              target="_blank"
              rel="noreferrer"
              className="group inline-flex shrink-0 items-center gap-1.5 transition-colors hover:text-emerald-300 hover:underline underline-offset-4"
            >
              <span>WIPO Global Award 2025</span>
              <ExternalLink className="size-2.5 opacity-50 transition-opacity group-hover:opacity-100" />
            </a>
            <a
              href="https://www.instagram.com/p/DbW4SIGhjDK/"
              target="_blank"
              rel="noreferrer"
              className="group inline-flex shrink-0 items-center gap-1.5 transition-colors hover:text-emerald-300 hover:underline underline-offset-4"
            >
              <span>ClimateLaunchpad Australia National Winner 2026</span>
              <ExternalLink className="size-2.5 opacity-50 transition-opacity group-hover:opacity-100" />
            </a>
          </div>
        </div>
        <div className="border-b border-slate-200/70 bg-[#f7faf9]/92 backdrop-blur-2xl dark:border-white/10 dark:bg-[#0a1214]/92">
          <div className="mx-auto flex h-20 sm:h-24 max-w-7xl items-center justify-between px-6">
            <Logo />
            <nav className="hidden items-center gap-0.5 xl:flex">
              {navigation.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) =>
                    `relative rounded-lg px-3.5 py-2 text-[13px] font-medium transition-all duration-200 ${isActive
                      ? "bg-emerald-50/80 text-emerald-800 after:absolute after:inset-x-1.5 after:bottom-0 after:h-[2px] after:rounded-full after:bg-emerald-500 dark:bg-emerald-500/15 dark:text-emerald-300"
                      : "text-slate-500 hover:bg-slate-100/80 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-white/8 dark:hover:text-white"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <a href={livePlatformUrl} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-1.5 rounded-lg border border-emerald-200/60 bg-emerald-50/50 px-3.5 py-2 text-[13px] font-medium text-emerald-700 transition-all duration-200 hover:border-emerald-300 hover:bg-emerald-100/80 hover:text-emerald-800 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:hover:border-emerald-400/40 dark:hover:bg-emerald-400/20">
                Live Platform <ExternalLink className="size-3" />
              </a>
            </nav>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <div className="hidden sm:block">
                <Link to="/start-pilot"><Button size="sm">Start a Pilot <ArrowUpRight className="size-3.5" /></Button></Link>
              </div>
              <button className="grid size-10 place-items-center rounded-full border border-slate-200 dark:border-white/15 xl:hidden" onClick={() => setOpen(true)} aria-label="Open navigation"><Menu className="size-5" /></button>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[80] overflow-y-auto bg-[#061a1d] p-6 text-white xl:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="flex items-center justify-between"><Logo variant="dark" /><button className="grid size-11 place-items-center rounded-full border border-white/15" onClick={() => setOpen(false)} aria-label="Close navigation"><X /></button></div>
            <div className="mt-12 grid gap-1">
              {[{ label: "Home", href: "/" }, ...navigation, { label: "Global Initiative", href: "/initiative" }, { label: "Partners", href: "/partners" }, { label: "Gallery", href: "/gallery" }, { label: "Contact", href: "/contact" }, { label: "Pilot Program", href: "/pilot" }, { label: "Start a Pilot", href: "/start-pilot" }].map((item, i) => (
                <motion.div key={`${item.href}-${item.label}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * .03 }}>
                  <Link to={item.href} className="block border-b border-white/10 py-3.5 text-xl font-medium tracking-tight">{item.label}</Link>
                </motion.div>
              ))}
              <a href={livePlatformUrl} target="_blank" rel="noreferrer" className="mt-4 flex items-center justify-between rounded-2xl bg-emerald-300 p-4 font-semibold text-slate-950">Open smartdrain.info <ExternalLink className="size-4" /></a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <main>{children}</main>

      <footer className="bg-[#04181b] px-6 py-16 text-white">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1.3fr_.7fr_.7fr_1fr]">
          <div>
            <Logo variant="dark" />
            <p className="mt-5 max-w-sm text-sm leading-6 text-white/55">Stop litter. Monitor the flow. Act before the flood. Smart Drain combines retrofit waste interception with IoT-enabled visibility for more resilient stormwater management.</p>
            <a href={livePlatformUrl} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-emerald-200">smartdrain.info <ExternalLink className="size-4" /></a>
          </div>
          <div><div className="text-xs font-semibold uppercase tracking-[.16em] text-white/40">Explore</div><div className="mt-4 grid gap-3 text-sm text-white/70"><Link to="/technology">Technology</Link><Link to="/products">Products</Link><Link to="/deployments">Deployments</Link><Link to="/pilot">Pilot Program</Link><Link to="/start-pilot">Start a Pilot</Link></div></div>
          <div><div className="text-xs font-semibold uppercase tracking-[.16em] text-white/40">Company</div><div className="mt-4 grid gap-3 text-sm text-white/70"><Link to="/development">Development</Link><Link to="/about">About</Link><Link to="/team">Team</Link><Link to="/partners">Partners</Link><Link to="/blog">Blog</Link></div></div>
          <div><div className="text-xs font-semibold uppercase tracking-[.16em] text-white/40">Connect</div><div className="mt-4 grid gap-3 text-sm text-white/70"><a className="flex items-center gap-2" href="mailto:info@smartdrain.network"><Mail className="size-4" /> info@smartdrain.network</a><Link className="flex items-center gap-2" to="/contact"><MessageCircle className="size-4" /> Pilots & partnerships</Link></div></div>
        </div>
        <div className="mx-auto mt-14 flex max-w-7xl flex-col gap-3 border-t border-white/10 pt-6 text-xs text-white/35 sm:flex-row sm:items-center sm:justify-between"><span>© {new Date().getFullYear()} Smart Drain / Urban Inventors Pty Ltd.</span><span>Physical protection + digital intelligence.</span></div>
      </footer>
    </div>
  )
}
